import {
  faCog,
  faEllipsisVertical,
  faFingerprint,
  faTrashCanCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import IWContextMenu from 'shared/components/thunderbolt/IWContextMenu'
import IWContextMenuItem from 'shared/components/thunderbolt/IWContextMenuItem'
import IWContextDivider from 'shared/components/thunderbolt/IWContextMenuItemDivider'
import IWGeneralBadge from 'shared/components/thunderbolt/IWGeneralBadge'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import {
  deleteIbtContract,
  getAdminInternalBilateralContracts,
} from 'shared/loadSchedulingClient'
import styled from 'styled-components'
import { InternalBilateralContract } from 'tools/loadScheduling/types'
import DeleteIbtContractModal from './DeleteIbtContractModal'
import LsAdminPageSectionHeader from './LsAdminPageSectionHeader'
import { lsAdminIBTContractsTableSchema } from './tableSchemas'

const StyledBadgeDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

type TableItem = {
  market: string
  brand: string
  zone: string
}

const tableColumns = lsAdminIBTContractsTableSchema

const USE_QUERY_KEY = 'ls-admin-ibt-contracts'

export const LsAdminIbtContracts = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [items, setItems] = useState<TableItem[]>([])
  const [ibtContractIdToDelete, setIbtContractConfigIdToDelete] = useState<
    string | undefined
  >(undefined)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: [USE_QUERY_KEY],
    queryFn: async () => getAdminInternalBilateralContracts(),
  })

  const deleteMutation = useMutation({
    mutationKey: [USE_QUERY_KEY, 'create'],
    mutationFn: (id: string) => deleteIbtContract(id),
    onSuccess: () => queryClient.invalidateQueries([USE_QUERY_KEY]),
  })

  const mapper = (ibts: InternalBilateralContract[]) => {
    return orderBy(ibts, ['market', 'brand', 'zone']).map((item) => ({
      market: item.market,
      brand: item.brand,
      zone: item.zone,
      contractName: item.contractName,
      contractId: item.contractId,
      locationName: item.locationName,
      location: item.location,
      buyOrSell: item.buyOrSell,
      buyerName: item.buyerName,
      buyerQseCode: item.buyerQseCode,
      sellerName: item.sellerName,
      sellerQseCode: item.sellerQseCode,
      counterparty: item.counterparty,
      isConfigActive: (
        <StyledBadgeDiv>
          <IWGeneralBadge
            label={t(`common.${item?.isConfigActive ? 'active' : 'disable'}`)}
            color={item?.isConfigActive ? 'primary' : 'alert'}
          />
        </StyledBadgeDiv>
      ),
      status: (
        <StyledBadgeDiv>
          <IWGeneralBadge
            label={t(
              `common.${item.isCredentialActive ? 'active' : 'disable'}`,
            )}
            color={item.isCredentialActive ? 'primary' : 'alert'}
          />
        </StyledBadgeDiv>
      ),
      contextMenu: (
        <IWContextMenu
          positions={['bottom', 'top']}
          menu={[
            <IWContextMenuItem
              as="routerLink"
              key={`ls-ibt-view-configs-${item.configId}`}
              label={t('loadScheduling.contextMenus.viewConfigs')}
              icon={faCog}
              to={`/admin/load-scheduling/configs?configId=${item.configId}`}
            />,

            <IWContextMenuItem
              as="routerLink"
              key={`ls-ibt-view-creds-${item.credentialId}`}
              data-testid={`ls-ibt-view-creds-${item.credentialId}`}
              label={t('loadScheduling.contextMenus.viewCredentials')}
              icon={faFingerprint}
              to={`/admin/load-scheduling/credentials?configId=${item.configId}`}
            />,

            <IWContextDivider />,

            <IWContextMenuItem
              as="button"
              key={`ls-la-delete-${item.configId}`}
              data-testid={`ls-la-delete-${item.configId}`}
              label={t('button.delete')}
              icon={faTrashCanCheck}
              onClick={() => {
                setIbtContractConfigIdToDelete(item.ibtContractId)
                setIsDeleteModalOpen(true)
              }}
            />,
          ]}
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </IWContextMenu>
      ),
    }))
  }

  useEffect(() => {
    if (data?.length) {
      setItems(mapper(data))
    }
  }, [data])

  return (
    <main>
      <LsAdminPageSectionHeader
        header={t('loadScheduling.titles.adminIbtContracts')}
        desc={t('loadScheduling.descriptions.adminIbtContracts')}
        buttonLabel={t('loadScheduling.labels.ibtContracts')}
        onButtonClick={() => {
          navigate('/admin/load-scheduling/ibt-contracts/create')
        }}
      >
        {Boolean(items?.length) && !isLoading && (
          <IWGenericTable
            border
            isLoading={isLoading}
            items={items}
            columns={tableColumns}
            initialHiddenColumns={[
              'locationName',
              'location',
              'buyerName',
              'sellerName',
            ]}
            showColumnSettings
          />
        )}

        <DeleteIbtContractModal
          isModalOpen={isDeleteModalOpen}
          ibtContractId={ibtContractIdToDelete}
          onCancel={() => setIsDeleteModalOpen(false)}
          onDelete={async (id?: string) => {
            if (!id) {
              return
            }

            await deleteMutation.mutateAsync(id)
            setIsDeleteModalOpen(false)
          }}
        />
      </LsAdminPageSectionHeader>
    </main>
  )
}
